import React, { useState, useEffect, useMemo } from 'react';
import PetchemExportButton from './PetchemExportButton';
import AIPanel from './AIPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContractsData, dataService, PetchemData, apiClient } from "@rti/shared";
import { formatDate } from "@/utils/commonUtils";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  Calendar,
  BarChart3,
  TrendingUp,
  List,
  Table,
  Download,
  Eye,
  EyeOff,
  ChevronDown,
} from "lucide-react";
import { trackUserEvent } from "@/utils/userTracking";

export interface FeedstockDetailsProps {}

interface FeedstockHistoricalData {
  date: string;
  feedstock: string;
  feedstockKey: string;
  value: string;
  numericValue: number;
  isContract: boolean;
  formattedDate: string;
  month: number;
  year: number;
  day: number;
}

interface TabularData {
  date: string;
  formattedDate: string;
  values: Record<string, string>;
}

type SortField = 'date' | 'feedstock' | 'value';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'historical' | 'tabular';

const FeedstockDetails: React.FC<FeedstockDetailsProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [petchemData, setPetchemData] = useState<PetchemData | null>(null);
  const [contractsData, setContractsData] = useState<ContractsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [historicalData, setHistoricalData] = useState<FeedstockHistoricalData[]>([]);
  const [tabularData, setTabularData] = useState<TabularData[]>([]);
  const [filteredData, setFilteredData] = useState<FeedstockHistoricalData[]>([]);
  const [filteredTabularData, setFilteredTabularData] = useState<TabularData[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>(() => {
    const now = new Date();
    return now.getFullYear().toString();
  });
  const [selectedDay, setSelectedDay] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [feedstockType, setFeedstockType] = useState<'spot' | 'contract'>('spot');
  const [viewMode, setViewMode] = useState<ViewMode>('tabular');
  const [dataError, setDataError] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const topScrollRef = React.useRef<HTMLDivElement>(null);

  const replaceList = {
    'us': '$US',
    'ca': 'CA$',
    'rgp': 'RGP',
    'pgp': 'PGP',
    'vam': 'VAM',
    'wti': 'WTI',
  };

  useEffect(() => {
    const storageKey = `feedstock-columns-${feedstockType}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved column visibility:', e);
      }
    }
  }, [feedstockType]);

  const toggleColumnVisibility = (columnName: string) => {
    const newVisibility = {
      ...visibleColumns,
      [columnName]: !visibleColumns[columnName]
    };
    setVisibleColumns(newVisibility);
    const storageKey = `feedstock-columns-${feedstockType}`;
    localStorage.setItem(storageKey, JSON.stringify(newVisibility));
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const feedstockParam = urlParams.get('feedstock');
    if (feedstockParam === 'spot' || feedstockParam === 'contract') {
      setFeedstockType(feedstockParam);
    } else {
      setFeedstockType('spot');
    }
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setDataError(null);

        const [petchemRes, contractsRes] = await Promise.all([
          dataService.getPetchemData(),
          dataService.getContractsData(),
        ]);

        setPetchemData(petchemRes);
        setContractsData(contractsRes);
      } catch (error) {
        console.error('Failed to fetch feedstock data:', error);
        setDataError('Failed to load feedstock data');
        setPetchemData(null);
        setContractsData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatValue = (value: any, feedstockKey: string): string => {
    if (value == null || isNaN(parseFloat(value))) return 'N/A';

    const numValue = parseFloat(value);
    if (feedstockKey && feedstockKey.includes("us")) {
      return '$' + numValue.toFixed(3);
    }
    return numValue > 1
      ? '$' + numValue.toFixed(2)
      : '$' + numValue.toFixed(4);
  };

  const formatFeedstockName = (feedstockKey: string, isContract: boolean = false): string => {
    let nameForDisplay = isContract ? feedstockKey.replace('_contract', '') : feedstockKey;

    return nameForDisplay
      .replace(/_/g, ' ')
      .replace(new RegExp(Object.keys(replaceList).join('|'), 'g'), match => replaceList[match as keyof typeof replaceList])
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const processData = (response: PetchemData | any[], isContract: boolean = false): {
    historical: FeedstockHistoricalData[];
    tabular: TabularData[]
  } => {
    if (!Array.isArray(response)) {
      console.error('Data is not an array:', response);
      setDataError('Invalid data format received');
      return { historical: [], tabular: [] };
    }

    const processedHistorical: FeedstockHistoricalData[] = [];
    const tabularMap: Record<string, Record<string, string>> = {};

    try {
      response.forEach((entry: any) => {
        if (!entry.date) return;

        let entryDate: Date;
        if (typeof entry.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(entry.date)) {
          const [year, month, day] = entry.date.split('T')[0].split('-').map(Number);
          entryDate = new Date(year, month - 1, day);
        } else {
          entryDate = new Date(entry.date);
        }

        if (isNaN(entryDate.getTime())) return;

        const formatDateForDisplay = (date: string) => {
          if (isContract) {
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
              const [year, month] = date.split('T')[0].split('-').map(Number);
              const d = new Date(year, month - 1, 1);
              return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            }
            return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          }
          return date;
        };

        if (!tabularMap[entry.date]) {
          tabularMap[entry.date] = {};
        }

        Object.keys(entry).forEach(key => {
          if (key === 'date' || key === 'last_updated') return;

          const value = entry[key];
          if (value == null) return;

          if (isContract) {
            if (key.includes('_contract')) {
              const formattedName = formatFeedstockName(key, true);
              const formattedValue = formatValue(value, key);

              processedHistorical.push({
                date: entry.date,
                feedstock: formattedName,
                feedstockKey: key,
                value: formattedValue,
                numericValue: parseFloat(value) || 0,
                isContract: true,
                formattedDate: formatDateForDisplay(entry.date),
                month: entryDate.getMonth(),
                year: entryDate.getFullYear(),
                day: entryDate.getDate()
              });

              tabularMap[entry.date][formattedName] = formattedValue;
            }
          } else {
            if (!key.includes('_contract') && !key.includes('_ngas')) {
              const formattedName = formatFeedstockName(key, false);
              const formattedValue = formatValue(value, key);

              processedHistorical.push({
                date: entry.date,
                feedstock: formattedName,
                feedstockKey: key,
                value: formattedValue,
                numericValue: parseFloat(value) || 0,
                isContract: false,
                formattedDate: formatDateForDisplay(entry.date),
                month: entryDate.getMonth(),
                year: entryDate.getFullYear(),
                day: entryDate.getDate()
              });

              tabularMap[entry.date][formattedName] = formattedValue;
            }
          }
        });
      });

      const processedTabular: TabularData[] = Object.keys(tabularMap)
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
        .map(date => ({
          date,
          formattedDate: isContract
            ? new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
            : date,
          values: tabularMap[date]
        }));

      setDataError(null);
      return { historical: processedHistorical, tabular: processedTabular };
    } catch (error) {
      console.error('Error processing data:', error);
      setDataError('Error processing feedstock data');
      return { historical: [], tabular: [] };
    }
  };

  useEffect(() => {
    if (isLoading) return;

    let dataToProcess: PetchemData | any[] = [];
    let processed: { historical: FeedstockHistoricalData[], tabular: TabularData[] };

    try {
      if (feedstockType === 'contract') {
        dataToProcess = contractsData || [];
        processed = processData(dataToProcess, true);
      } else {
        dataToProcess = petchemData || [];
        processed = processData(dataToProcess, false);
      }

      trackUserEvent("feedstock_details", "Feedstock Details Viewed", { feedstockType: feedstockType });
      setHistoricalData(processed.historical);
      setTabularData(processed.tabular);
    } catch (error) {
      console.error('Error in data processing:', error);
      setDataError('Failed to process feedstock data');
      setHistoricalData([]);
      setTabularData([]);
    }
  }, [petchemData, contractsData, feedstockType, isLoading]);

  const { availableYears, availableMonths, availableDays } = useMemo(() => {
    const years = Array.from(new Set(historicalData.map(item => item.year))).sort((a, b) => b - a);
    const months = Array.from(new Set(historicalData.map(item => item.month))).sort((a, b) => a - b);
    const days = Array.from(new Set(historicalData.map(item => item.day))).sort((a, b) => a - b);

    return {
      availableYears: years,
      availableMonths: months,
      availableDays: days
    };
  }, [historicalData]);

  const uniqueFeedstocks = useMemo(() => {
    if (tabularData.length === 0) return [];

    const feedstocks = new Set<string>();
    tabularData.forEach(row => {
      Object.keys(row.values).forEach(feedstock => feedstocks.add(feedstock));
    });

    const feedstockArray = Array.from(feedstocks);

    if (feedstockType === 'spot') {
      const spotOrderRaw = [
        'spot_ethylene','ethane','propane','spot_rgp','spot_pgp','spot_benzene','spot_styrene','spot_butadiene','wti_crude_oil','brent_crude_oil','nat_gas','euro_to_us','us_to_ca','vam'
      ];

      const spotOrderFormatted = spotOrderRaw.map(key => formatFeedstockName(key, false));

      return feedstockArray.sort((a, b) => {
        const indexA = spotOrderFormatted.indexOf(a);
        const indexB = spotOrderFormatted.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
    }

    return feedstockArray.sort();
  }, [tabularData, feedstockType]);

  useEffect(() => {
    if (uniqueFeedstocks.length === 0) return;

    setVisibleColumns(prev => {
      const updated = { ...prev };
      let hasChanges = false;

      uniqueFeedstocks.forEach(feedstock => {
        if (!(feedstock in updated)) {
          updated[feedstock] = true;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        const storageKey = `feedstock-columns-${feedstockType}`;
        localStorage.setItem(storageKey, JSON.stringify(updated));
      }

      return hasChanges ? updated : prev;
    });
  }, [uniqueFeedstocks, feedstockType]);

  const visibleFeedstocks = useMemo(() => {
    return uniqueFeedstocks.filter(feedstock => visibleColumns[feedstock] !== false);
  }, [uniqueFeedstocks, visibleColumns]);

  useEffect(() => {
    const parseDate = (dateStr: string): Date => {
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateStr);
    };

    let filtered = [...historicalData];

    if (selectedYear !== 'all') {
      filtered = filtered.filter(item => item.year === parseInt(selectedYear));
    }

    if (selectedMonth && selectedMonth !== 'all') {
      filtered = filtered.filter(item => item.month === parseInt(selectedMonth));
    }

    if (selectedDay && selectedDay !== 'all') {
      filtered = filtered.filter(item => item.day === parseInt(selectedDay));
    }

    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'date':
          aValue = parseDate(a.date).getTime();
          bValue = parseDate(b.date).getTime();
          break;
        case 'feedstock':
          aValue = a.feedstock.toLowerCase();
          bValue = b.feedstock.toLowerCase();
          break;
        case 'value':
          aValue = a.numericValue;
          bValue = b.numericValue;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredData(filtered);

    let filteredTabular = [...tabularData];

    if (selectedYear !== 'all') {
      filteredTabular = filteredTabular.filter(item => parseDate(item.date).getFullYear() === parseInt(selectedYear));
    }

    if (selectedMonth && selectedMonth !== 'all') {
      filteredTabular = filteredTabular.filter(item => parseDate(item.date).getMonth() === parseInt(selectedMonth));
    }

    if (selectedDay && selectedDay !== 'all') {
      filteredTabular = filteredTabular.filter(item => parseDate(item.date).getDate() === parseInt(selectedDay));
    }

    filteredTabular.sort((a, b) => {
      const aTime = parseDate(a.date).getTime();
      const bTime = parseDate(b.date).getTime();
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
    });

    setFilteredTabularData(filteredTabular);
  }, [historicalData, tabularData, selectedMonth, selectedYear, selectedDay, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };
  const capitalizeFirst = (str: string): any =>
    str.charAt(0).toUpperCase() + str.slice(1);

  const getTypeTitle = () => {
    return feedstockType === 'contract'
      ? 'Feedstock Contract Prices - ' + capitalizeFirst(viewMode) + ' View'
      : 'Daily PetroChemical Feedstock Spot Prices - ' + capitalizeFirst(viewMode) + ' View';
  };

  const getMonthName = (monthIndex: number) => {
    return new Date(2000, monthIndex).toLocaleString('default', { month: 'long' });
  };

  useEffect(() => {
    const handleScroll = () => {
      const container = tableContainerRef.current;
      if (container) {
        if (topScrollRef.current) {
          const innerDiv = topScrollRef.current.querySelector('div > div') as HTMLElement;
          if (innerDiv) {
            innerDiv.style.width = `${container.scrollWidth}px`;
          }
        }
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);

      const observer = new MutationObserver(handleScroll);
      observer.observe(container, { childList: true, subtree: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        observer.disconnect();
      };
    }
  }, [filteredData, filteredTabularData, visibleFeedstocks]);

  const escapeCsv = (value: any) => {
    const s = value == null ? '' : String(value);
    return '"' + s.replace(/"/g, '""') + '"';
  };

  const buildHistoricalCsv = (records: FeedstockHistoricalData[]) => {
    const rows: string[] = [];
    rows.push(['Date', 'Feedstock', 'Price'].map(escapeCsv).join(','));
    for (const item of records) {
      const dateStr = feedstockType === 'contract' ? item.formattedDate : formatDate(item.date);
      rows.push([dateStr, item.feedstock, item.value].map(escapeCsv).join(','));
    }
    return rows.join('\n');
  };

  const buildTabularCsv = (records: TabularData[], headers: string[]) => {
    const rows: string[] = [];
    rows.push(['Date', ...headers].map(escapeCsv).join(','));
    for (const row of records) {
      const dateStr = feedstockType === 'contract' ? row.formattedDate : formatDate(row.date);
      const values = headers.map((f) => row.values[f] || '');
      rows.push([dateStr, ...values].map(escapeCsv).join(','));
    }
    return rows.join('\n');
  };

  const handleDownloadCsv = () => {
    try {
      const hasData = viewMode === 'historical' ? filteredData.length > 0 : filteredTabularData.length > 0;
      if (!hasData) return;

      trackUserEvent("feedstock_details", "Feedstock CSV Downloaded", { feedstockType: feedstockType, viewMode: viewMode });
      const csvBody = viewMode === 'historical'
        ? buildHistoricalCsv(filteredData)
        : buildTabularCsv(filteredTabularData, uniqueFeedstocks);
      const csvWithBom = '\uFEFF' + csvBody;
      const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const ts = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const filename = `export-feedstock-${feedstockType}-${viewMode}-${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV download failed', e);
    }
  };

  const handleDownloadAllCsv = () => {
    try {
      const csvBody = viewMode === 'historical'
        ? buildHistoricalCsv(historicalData)
        : buildTabularCsv(tabularData, uniqueFeedstocks);
      if (!csvBody) return;
      const csvWithBom = '\uFEFF' + csvBody;
      const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const ts = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const filename = `export-feedstock-${feedstockType}-${viewMode}-all-${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.csv`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV download failed', e);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full h-[500px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-rs-off-white">Loading feedstock data...</p>
        </div>
      </div>
    );
  }

  if (dataError || '') {
    return (
      <div className="flex items-center justify-center h-full h-[500px]">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <Calendar className="h-16 w-16 mx-auto mb-2" />
            <p className="text-lg font-semibold">Data Loading Error</p>
            <p className="text-sm">{dataError || ''}</p>
          </div>
          <button
            data-testid="retry-button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasDataForDownload = viewMode === 'historical' ? filteredData.length > 0 : filteredTabularData.length > 0;

  return (
    <div data-testid="feedstock-details-page" className="feedstock-details p-6 h-full ">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <div className="content-toolbar">
            <button
              data-testid="back-to-landing-page-button"
              className="jet"
              onClick={() => (window.location.href = "/dashboard")}
            >
              <ArrowLeft className="inline" />
              &ensp;Back to Landing Page
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-rs-dark-green mr-3" />
            <h1 data-testid="page-title" className="text-3xl font-bold ">{getTypeTitle()}</h1>
          </div>
          <PetchemExportButton />
        </div>
        <p className="text-rs-off-white">
          Complete historical data for all {feedstockType} feedstock prices by
          month and year
        </p>
      </div>

      {/* Type Toggle Buttons */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 mb-6">
        <button
          data-testid="feedstock-spot-prices-button"
          onClick={() => {
            if (feedstockType === "spot") return;
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("feedstock", "spot");
            navigate(newUrl.pathname + newUrl.search);
          }}
          disabled={feedstockType === "spot"}
          className={`flex items-center justify-center px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base ${
            feedstockType !== "spot"
              ? "bg-rs-rb-gradient text-white"
              : "bg-rs-off-black text-rs-off-white border border-rs-jet-light !cursor-not-allowed opacity-80"
          }`}
          aria-pressed={feedstockType === "spot"}
        >
          <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
          <span className="hidden md:inline">Feedstock </span>Spot<span className="hidden md:inline"> Prices</span>
        </button>
        <button
          data-testid="feedstock-contract-prices-button"
          onClick={() => {
            if (feedstockType === "contract") return;
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("feedstock", "contract");
            navigate(newUrl.pathname + newUrl.search);
          }}
          disabled={feedstockType === "contract"}
          className={`flex items-center justify-center px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base ${
            feedstockType !== "contract"
              ? "bg-rs-rb-gradient  text-white"
              : "bg-rs-off-black text-rs-off-white border border-rs-jet-light !cursor-not-allowed opacity-80"
          }`}
          aria-pressed={feedstockType === "contract"}
        >
          <TrendingUp className="h-4 w-4 mr-1 sm:mr-2" />
          <span className="hidden md:inline">Feedstock </span>Contract<span className="hidden md:inline"> Prices</span>
        </button>
      </div>

      <AIPanel
        fetchFn={(force) => apiClient.getAIInsights(feedstockType === 'spot' ? 'feedstock-spot' : 'feedstock-contract', force)}
        regenerateFn={(overrides) => apiClient.regenerateAiInsights(feedstockType === 'spot' ? 'feedstock-spot' : 'feedstock-contract', overrides)}
        context={feedstockType === 'spot' ? 'feedstock-spot' : 'feedstock-contract'}
        resetKey={feedstockType}
      />

      {/* View Mode Toggle and Filters Combined Row */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
          <button
            data-testid="tabular-view-button"
            onClick={() => {
              setViewMode("tabular");
              trackUserEvent("feedstock_view_mode", "View Changed to Tabular", { feedstockType: feedstockType });
            }}
            className={`flex items-center justify-center px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base ${
              viewMode === "tabular"
                ? "bg-rs-green !text-black border border-rs-jet-light !cursor-not-allowed opacity-30"
                : "bg-rs-green !text-black hover:bg-rs-green/80"
            }`}
          >
            <Table className="h-4 w-4 mr-1 sm:mr-2" />
            Tabular<span className="hidden md:inline"> View</span>
          </button>
          <button
            data-testid="historical-view-button"
            onClick={() => {
              setViewMode("historical");
              trackUserEvent("feedstock_view_mode", "View Changed to Historical", { feedstockType: feedstockType });
            }}
            className={`flex items-center justify-center px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base ${
              viewMode === "historical"
                ? "bg-rs-green !text-black border border-rs-jet-light !cursor-not-allowed opacity-30"
                : "bg-rs-green !text-black hover:bg-rs-green/80"
            }`}
          >
            <List className="h-4 w-4 mr-1 sm:mr-2" />
            Historical <span className="hidden md:inline"> View</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="flex items-center gap-2">
            <label className="text-sm sm:text-base font-medium text-rs-off-white whitespace-nowrap">
              Month:
            </label>
            <select
              data-testid="month-filter-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="flex-1 px-2 py-2 text-sm sm:text-base font-light border border-rs-jet-light rounded focus:outline-none focus:ring-2 focus:ring-rs-dark-green"
            >
              <option value="all">All</option>
              {availableMonths.map((month) => (
                <option key={month} value={month.toString()}>
                  {getMonthName(month)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm sm:text-base font-medium text-rs-off-white whitespace-nowrap">
              Day:
            </label>
            <select
              data-testid="day-filter-select"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="flex-1 px-2 py-2 text-sm sm:text-base font-light border border-rs-jet-light rounded focus:outline-none focus:ring-2 focus:ring-rs-dark-green"
            >
              <option value="all">All</option>
              {availableDays.map((day) => (
                <option key={day} value={day.toString()}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm sm:text-base font-medium text-rs-off-white whitespace-nowrap">
              Year:
            </label>
            <select
              data-testid="year-filter-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="flex-1 px-2 py-2 text-sm sm:text-base font-light border border-rs-jet-light rounded focus:outline-none focus:ring-2 focus:ring-rs-dark-green"
            >
              <option value="all">All</option>
              {availableYears.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {viewMode === "historical" && (
            <div className="flex items-center gap-2">
              <label className="text-sm sm:text-base font-medium text-rs-off-white whitespace-nowrap">
                Sort:
              </label>
              <select
                data-testid="sort-field-select"
                value={sortField}
                onChange={(e) => setSortField(e.target.value as SortField)}
                className="flex-1 px-2 py-2 text-sm sm:text-base font-light border border-rs-jet-light rounded focus:outline-none focus:ring-2 focus:ring-rs-dark-green"
              >
                <option value="date">Date</option>
                <option value="feedstock">Feedstock</option>
                <option value="value">Price</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="bg-rs-off-black rounded-lg shadow overflow-hidden">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-0">
            {viewMode === "historical"
              ? "Historical Feedstock Data"
              : "Tabular Feedstock Data"}
          </h3>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            {viewMode === "tabular" && (
              <div className="w-full sm:w-auto order-1 sm:order-none">
                <div className="relative">
                  <button
                    onClick={() => setShowColumnMenu(!showColumnMenu)}
                    className="w-full sm:w-auto px-2 sm:px-3 py-2 rounded text-xs sm:text-sm font-medium transition-colors bg-rs-jet text-rs-off-white hover:bg-rs-jet-light flex items-center justify-center gap-1 sm:gap-2"
                    title="Show/Hide Columns"
                  >
                    <Eye size={16} className="sm:w-5 sm:h-5" />
                    <span>Show/Hide Columns</span>
                    <ChevronDown size={14} className="sm:w-4 sm:h-4" />
                  </button>
                  {showColumnMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-30"
                        onClick={() => setShowColumnMenu(false)}
                      />
                      <div className="absolute left-0 sm:right-0 sm:left-auto mt-2 w-full sm:w-64 bg-rs-jet border border-rs-jet-light rounded-lg shadow-lg z-40 max-h-96 overflow-y-auto">
                        <div className="p-3 border-b border-rs-jet-light">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-rs-off-white">Column Visibility</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const allVisible: Record<string, boolean> = {};
                                uniqueFeedstocks.forEach(f => allVisible[f] = true);
                                setVisibleColumns(allVisible);
                                const storageKey = `feedstock-columns-${feedstockType}`;
                                localStorage.setItem(storageKey, JSON.stringify(allVisible));
                              }}
                              className="text-xs text-rs-green hover:text-rs-dark-green"
                            >
                              Show All
                            </button>
                          </div>
                          <div className="text-xs text-rs-off-white/70">
                            {visibleFeedstocks.length} of {uniqueFeedstocks.length} visible
                          </div>
                        </div>
                        <div className="p-2">
                          {uniqueFeedstocks.map((feedstock) => (
                            <label
                              key={feedstock}
                              className="flex items-center gap-2 px-2 py-2 hover:bg-rs-jet-light rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={visibleColumns[feedstock] !== false}
                                onChange={() => toggleColumnVisibility(feedstock)}
                                className="w-4 h-4 text-rs-green bg-rs-off-black border-rs-jet-light rounded focus:ring-rs-green focus:ring-2"
                              />
                              <span className="text-sm text-rs-off-white flex items-center gap-2">
                                {visibleColumns[feedstock] !== false ? (
                                  <Eye size={14} className="text-rs-green" />
                                ) : (
                                  <EyeOff size={14} className="text-rs-off-white/50" />
                                )}
                                {feedstock}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 sm:flex order-2 sm:order-none">
              <button
                data-testid="export-selected-data-button"
                onClick={handleDownloadCsv}
                disabled={!hasDataForDownload}
                className={`px-2 sm:px-3 py-2 rounded text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                  hasDataForDownload
                    ? "bg-rs-green !text-black hover:bg-rs-green/80"
                    : "bg-rs-jet text-rs-off-white opacity-60 cursor-not-allowed"
                }`}
                title={hasDataForDownload ? "Download selected CSV" : "No data to download"}
              >
                <Download size={16} className="sm:w-5 sm:h-5" />
                  <span className="hidden md:inline">Export </span>Selected
              </button>
              <button
                data-testid="export-all-data-button"
                onClick={handleDownloadAllCsv}
                className="px-2 sm:px-3 py-2 rounded text-xs sm:text-sm font-medium transition-colors bg-rs-rb-gradient text-white hover:bg-rs-jet flex items-center justify-center gap-1"
                title="Download all data"
              >
                <Download size={16} className="sm:w-5 sm:h-5" />
                <span className="hidden md:inline">Export </span>All
              </button>
            </div>
          </div>
        </div>

        <div
          ref={topScrollRef}
          className="overflow-x-auto overflow-y-hidden -mx-3 sm:mx-0 mb-2"
          onScroll={(e) => {
            if (tableContainerRef.current && tableContainerRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
              tableContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <div style={{ height: '1px', width: 'max-content', minWidth: '100%' }}>
            <div style={{ width: tableContainerRef.current?.scrollWidth || '100%', height: '1px' }}></div>
          </div>
        </div>

        <div
          ref={tableContainerRef}
          className="overflow-x-auto -mx-3 sm:mx-0 scroll-smooth"
          onScroll={(e) => {
            if (topScrollRef.current && topScrollRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
              topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          {viewMode === "historical" ? (
            <table data-testid="historical-data-table" className="min-w-full divide-y divide-rs-jet">
              <thead className="bg-rs-off-black">
                <tr>
                  <th
                    data-testid="sort-by-date"
                    className="px-6 py-3 text-left text-sm font-medium  uppercase tracking-wider cursor-pointer hover:bg-rs-dark-green bg-rs-off-black"
                    onClick={() => handleSort("date")}
                  >
                    <div className="flex items-center">
                      Date
                      {getSortIcon("date")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left  text-sm font-medium  uppercase tracking-wider cursor-pointer hover:bg-rs-dark-green bg-rs-off-black"
                    onClick={() => handleSort("feedstock")}
                  >
                    <div className="flex items-center">
                      Feedstock
                      {getSortIcon("feedstock")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left font-medium uppercase tracking-wider cursor-pointer hover:bg-rs-dark-green bg-rs-off-black"
                    onClick={() => handleSort("value")}
                  >
                    <div className="flex items-center">
                      Price
                      {getSortIcon("value")}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-rs-off-black divide-y divide-rs-jet ">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center ">
                      {historicalData.length === 0
                        ? "No feedstock data available"
                        : "No data available for the selected filters"}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr
                      key={`${item.date}-${item.feedstockKey}-${index}`}
                      className="hover:bg-rs-dark-green hover:text-rs-off-black"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm ">
                        {feedstockType === "contract"
                          ? item.formattedDate
                          : formatDate(item.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium ">
                        {item.feedstock}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm  ">
                        {item.value}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table data-testid="tabular-data-table" className="min-w-full divide-y divide-rs-jet">
              <thead className="bg-rs-off-black">
                <tr className="font-light">
                  <th
                    data-testid="sort-by-date"
                    className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider cursor-pointer sticky left-0 bg-rs-off-black"
                    onClick={() => handleSort("date")}
                  >
                    <div className="flex items-center">
                      Date
                      {getSortIcon("date")}
                    </div>
                  </th>
                  {visibleFeedstocks.map((feedstock) => (
                    <th
                      key={feedstock}
                      className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider min-w-[120px] bg-rs-off-black text-rs-off-white"
                    >
                      <div className="whitespace-nowrap">
                        {feedstock}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-rs-off-black divide-y divide-rs-jet ">
                {filteredTabularData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleFeedstocks.length + 1}
                      className="px-6 py-12 text-center "
                    >
                      {tabularData.length === 0
                        ? "No feedstock data available"
                        : "No data available for the selected filters"}
                    </td>
                  </tr>
                ) : (
                  filteredTabularData.map((row, index) => (
                    <tr
                      key={`${row.date}-${index}`}
                      className="hover:bg-rs-dark-green hover:text-rs-off-black group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-light sticky left-0 bg-rs-off-black group-hover:bg-rs-dark-green group-hover:text-rs-off-black z-10">
                        {feedstockType === "contract"
                          ? row.formattedDate
                          : formatDate(row.date)}
                      </td>
                      {visibleFeedstocks.map((feedstock) => (
                        <td
                          key={feedstock}
                          className="px-3 py-4 whitespace-nowrap text-sm  font-light text-center"
                        >
                          {row.values[feedstock] || "N/A"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Table Footer */}
        {((viewMode === "historical" && filteredData.length > 0) ||
          (viewMode === "tabular" && filteredTabularData.length > 0)) && (
          <div className="px-6 py-3 bg-rs-off-black border-t border-gray-200">
            <div className="flex justify-between items-center text-sm text-rs-off-white">
              <span>
                Displaying {feedstockType} prices{" "}
                {selectedYear !== 'all'
                  ? `for ${selectedYear}`
                  : "for all years"}
                {selectedMonth !== "all"
                  ? ` in ${getMonthName(parseInt(selectedMonth))}`
                  : ""}
                {selectedDay !== "all" ? ` on day ${selectedDay}` : ""} in{" "}
                {viewMode} view
              </span>

              <div className=" text-rs-off-white whitespace-nowrap" data-testid="data-count">
                {viewMode === "historical"
                  ? filteredData.length
                  : filteredTabularData.length}{" "}
                of{" "}
                {viewMode === "historical"
                  ? historicalData.length
                  : tabularData.length}{" "}
                records
              </div>

              <span>
                Sort: {sortField} (
                {sortDirection === "asc" ? "ascending" : "descending"})
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedstockDetails;
